<template>
  <nav :aria-label="ariaLabel">
    <b-pagination
      ref="pagination"
      class="mb-0"
      size="sm"
      :value="value"
      :total-rows="totalRows"
      :per-page="perPage"
      @input="$emit('input', $event)"
      @change="$emit('change', $event)"
    />
  </nav>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';

/**
 * <b-pagination> exposes the page list as a `menubar` of `menuitemradio` buttons
 * with a roving tabindex: every page but the current one gets tabindex="-1" and
 * is skipped by Tab. Pagination is not a menu, so here it is wrapped in a <nav>
 * and the menu semantics are stripped after every render.
 */
@Component
export default class AppPagination extends Vue {
  @Prop({ default: null }) readonly value!: number | null;

  @Prop({ default: 0 }) readonly totalRows!: number;

  @Prop({ default: 20 }) readonly perPage!: number;

  @Prop({ default: 'Pagination' }) readonly ariaLabel!: string;

  mounted() {
    this.normalizeA11y();
    (this.$refs.pagination as Vue).$on('hook:updated', this.normalizeA11y);
  }

  normalizeA11y() {
    const list = this.$el.querySelector('ul.pagination');
    if (!list) return;

    list.removeAttribute('role');
    list.removeAttribute('aria-label');

    Array.from(this.$el.querySelectorAll('li[role]')).forEach((item) => {
      // The ellipsis is decorative filler, the rest are plain list items
      if (item.getAttribute('role') === 'separator') item.setAttribute('aria-hidden', 'true');
      item.removeAttribute('role');
    });

    Array.from(this.$el.querySelectorAll('.page-link')).forEach((link) => {
      // Read the active page from the class, not from aria-checked: Vue will not
      // restore an attribute we removed here, because its value did not change
      // in the virtual DOM.
      const isCurrent = !!link.parentElement && link.parentElement.classList.contains('active');
      link.removeAttribute('role');
      link.removeAttribute('aria-checked');
      if (isCurrent) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
      // Disabled arrows render as <span> and stay out of the tab order
      if (link.tagName === 'BUTTON') link.setAttribute('tabindex', '0');
    });
  }
}
</script>
